import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Mail } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Logo } from '../components/Logo'
import { Button, Input, Text } from '../components/ui'
import { authClient } from '../lib/auth-client'

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
})

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const [globalError, setGlobalError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  async function onSubmit(data: ForgotPasswordForm) {
    setGlobalError(null)

    const { error } = await authClient.requestPasswordReset({
      email: data.email,
      redirectTo: `${window.location.origin}/reset-password`,
    })

    if (error) {
      setGlobalError(error.message ?? 'Could not send reset email. Please try again.')
      return
    }

    setSubmittedEmail(data.email)
  }

  if (submittedEmail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-stone">
            <Mail className="h-7 w-7 text-foreground" />
          </div>
          <Text variant="h1" className="mb-3">
            Check your inbox
          </Text>
          <Text variant="body" className="mb-2 text-foreground/60">
            If an account exists for
          </Text>
          <Text variant="body-sm" className="mb-6 font-semibold">
            {submittedEmail}
          </Text>
          <Text variant="body-sm" className="mb-8 text-foreground/60">
            we sent a link to reset your password. The link expires in 1 hour.
          </Text>
          <Button variant="outline" onClick={() => window.location.assign('/login')}>
            Back to login
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[480px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo className="mb-6" />
          <Text variant="h1" className="mb-2">
            Reset your password
          </Text>
          <Text variant="body-sm" className="text-foreground/60">
            Enter your email and we&apos;ll send you a link to choose a new password.
          </Text>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Email"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />

          {globalError && (
            <Text variant="body-sm" className="text-red-600">
              {globalError}
            </Text>
          )}

          <Button type="submit" fullWidth size="lg" loading={isSubmitting}>
            Send reset link
          </Button>
        </form>

        <Text variant="body-sm" className="mt-6 text-center text-foreground/60">
          Remember your password?{' '}
          <Link to="/login" className="font-medium text-foreground underline">
            Back to login
          </Link>
        </Text>
      </div>
    </div>
  )
}
